import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import type {
  Recipe,
  RecipeListItem,
  InterviewState,
  Project,
} from '@/types';

interface RecipeContextType {
  // State
  recipes: RecipeListItem[];
  categories: string[];
  selectedCategory: string | null;
  activeRecipe: Recipe | null;
  interviewState: InterviewState | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadRecipes: (category?: string) => Promise<void>;
  loadCategories: () => Promise<void>;
  selectCategory: (category: string | null) => void;
  selectRecipe: (idOrSlug: string) => Promise<void>;
  startInterview: (recipe: Recipe) => void;
  updateInterviewResponse: (fieldId: string, value: unknown) => void;
  advanceInterview: () => void;
  goBackInterview: () => void;
  completeInterview: () => Promise<Project>;
  cancelInterview: () => void;
  clearError: () => void;
}

const RecipeContext = createContext<RecipeContextType | undefined>(undefined);

export function RecipeProvider({ children }: { children: React.ReactNode }) {
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [activeRecipe, setActiveRecipe] = useState<Recipe | null>(null);
  const [interviewState, setInterviewState] = useState<InterviewState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRecipes = useCallback(async (category?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.listRecipes({ category });
      setRecipes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipes');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await api.listRecipeCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  const selectCategory = useCallback((category: string | null) => {
    setSelectedCategory(category);
    loadRecipes(category || undefined);
  }, [loadRecipes]);

  const selectRecipe = useCallback(async (idOrSlug: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const recipe = await api.getRecipe(idOrSlug);
      setActiveRecipe(recipe);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipe');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startInterview = useCallback((recipe: Recipe) => {
    setActiveRecipe(recipe);
    setInterviewState({
      recipe_id: recipe.id,
      current_step: 0,
      total_steps: recipe.interview_schema.steps.length,
      responses: {},
      is_complete: false,
    });
  }, []);

  const updateInterviewResponse = useCallback((fieldId: string, value: unknown) => {
    setInterviewState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        responses: {
          ...prev.responses,
          [fieldId]: value,
        },
      };
    });
  }, []);

  const advanceInterview = useCallback(() => {
    setInterviewState(prev => {
      if (!prev || !activeRecipe) return prev;

      const nextStep = prev.current_step + 1;
      const isComplete = nextStep >= activeRecipe.interview_schema.steps.length;

      return {
        ...prev,
        current_step: isComplete ? prev.current_step : nextStep,
        is_complete: isComplete,
      };
    });
  }, [activeRecipe]);

  const goBackInterview = useCallback(() => {
    setInterviewState(prev => {
      if (!prev || prev.current_step === 0) return prev;
      return {
        ...prev,
        current_step: prev.current_step - 1,
        is_complete: false,
      };
    });
  }, []);

  const completeInterview = useCallback(async (): Promise<Project> => {
    if (!activeRecipe || !interviewState) {
      throw new Error('No active interview');
    }

    setError(null);
    try {
      // Create project with recipe and interview data
      const project = await api.createProject({
        name: (interviewState.responses.business_name as string) ||
              (interviewState.responses.restaurant_name as string) ||
              (interviewState.responses.name as string) ||
              (interviewState.responses.headline as string) ||
              `${activeRecipe.name} Project`,
        recipe_id: activeRecipe.id,
        interview_data: interviewState.responses,
      });

      // Clear interview state
      setInterviewState(null);
      setActiveRecipe(null);

      return project;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      setError(message);
      throw err;
    }
  }, [activeRecipe, interviewState]);

  const cancelInterview = useCallback(() => {
    setInterviewState(null);
    setActiveRecipe(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <RecipeContext.Provider
      value={{
        recipes,
        categories,
        selectedCategory,
        activeRecipe,
        interviewState,
        isLoading,
        error,
        loadRecipes,
        loadCategories,
        selectCategory,
        selectRecipe,
        startInterview,
        updateInterviewResponse,
        advanceInterview,
        goBackInterview,
        completeInterview,
        cancelInterview,
        clearError,
      }}
    >
      {children}
    </RecipeContext.Provider>
  );
}

export function useRecipes() {
  const context = useContext(RecipeContext);
  if (context === undefined) {
    throw new Error('useRecipes must be used within a RecipeProvider');
  }
  return context;
}
